/**
 * US/NANP phone number formatting and validation for the consent modal's
 * mobile number field. Deliberately not a dependency (libphonenumber-js is
 * ~80KB minified for international parsing this field never needs) — see
 * docs/agents/dependencies.md.
 */

/**
 * Strips everything but digits, then drops a leading "1" country-code digit
 * once there are 11 digits, and caps at 10. Formatting and validation both
 * work off this normalized digit string rather than the raw input, so a
 * pasted `(212) 555-0123`, `+1 212 555 0123`, or `12125550123` all converge
 * on the same 10 digits.
 */
export const toDigits = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const withoutCountryCode =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return withoutCountryCode.slice(0, 10);
};

/**
 * Formats a raw (possibly partial) input as progressive "XXX-XXX-XXXX".
 * Never emits a trailing separator — `formatUsPhone('212-')` (what's left
 * after backspacing the 4th digit) must re-derive to `'212'`, not `'212-'`,
 * or the first backspace after a hyphen appears to do nothing.
 */
export const formatUsPhone = (value: string): string => {
  const digits = toDigits(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const isCompleteUsPhone = (value: string): boolean => {
  const digits = toDigits(value);
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
};

/** Converts a complete number to E.164 `+1XXXXXXXXXX`, or `null` if incomplete. */
export const toE164 = (value: string): string | null => {
  if (!isCompleteUsPhone(value)) return null;
  return `+1${toDigits(value)}`;
};
